import type { IResetPasswordCmd } from "@/data/commands/auth/ResetPasswordCmd";
import { Factory } from "@/data/dao/Factory";
import { AuthenticationDao } from "@/data/dao/service/AuthenticationDao";

import { logError } from "@/shared/logging/errors";
import { FloatLabel } from "@/shared/ui/FloatLabel";
import { Ripple } from "@/shared/ui/Ripple";

import { PasswordInput } from "@/app/elements/PasswordInput";
import { trans } from "@/app/localization/Trans";
import { Popup } from "@/app/ui/Popup";

export interface IPasswordResetConfig {
    token: string;
    id: string;
}

export class PasswordReset {
    private $element: JQuery;
    private $submitBtn: JQuery;
    private passwordInput: PasswordInput;
    private passwordRepeatInput: PasswordInput;
    private config: IPasswordResetConfig;

    constructor($element: JQuery, config: IPasswordResetConfig) {
        this.config = config;
        this.$element = $element;
        this.$element.find("> .title").html(trans.signin.setNewPassword);

        const form = this.$element.find("> .form").submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            if (this.$submitBtn.hasClass("loading")) return false;

            if (this.config.token === undefined) return false;

            if (this.config.id === undefined) return false;

            this.onResetPassword();

            return false;
        });

        this.passwordInput = new PasswordInput(true, true).appendTo(form.find("> .formrow.pw"));
        this.passwordRepeatInput = new PasswordInput(false, true, trans.signin.repeatPassword).appendTo(
            form.find("> .formrow.pwrepeat")
        );

        FloatLabel.bind(form);
        this.$submitBtn = Ripple.bind(
            form
                .find('button[type="submit"]')
                .html(trans.general.save)
                .click(() => {
                    form.submit();
                })
        );
    }

    private async onResetPassword() {
        if (this.$submitBtn.hasClass("loading")) return;

        const data: IResetPasswordCmd = {
            newPassword: this.passwordInput.getValue(),
            userId: this.config.id,
            token: this.config.token,
        };

        let hasError = false;
        if (!this.passwordInput.isValid()) {
            hasError = true;
            this.passwordInput.setError(true);
        } else {
            this.passwordInput.setError(false);
            if (this.passwordInput.getValue() !== this.passwordRepeatInput.getValue()) {
                hasError = true;
                this.passwordRepeatInput.setError(true);
            } else {
                this.passwordRepeatInput.setError(false);
            }
        }

        if (hasError) return;

        this.$submitBtn.prop("disabled", true).addClass("loading");
        try {
            await Factory.getInstance<AuthenticationDao>(AuthenticationDao).resetPassword(data);
            const form = this.$element.find("> .form").fadeOut("fast", "easeOutExpo", () => {
                form.html(
                    '<div class="success"><i class="fas fa-check-circle"></i><div class="msg">' +
                        trans.signin.resetSuccess +
                        "</div></div>"
                );
                form.fadeIn("fast", "easeOutExpo");
            });
        } catch (ex) {
            logError(ex);
            this.$submitBtn.prop("disabled", false).removeClass("loading");
            Popup.show(trans.signin.resetFailed);
        }
    }
}

export class EmailConfirm {
    constructor($element: JQuery, success: boolean) {
        if (success) $element.find("> .title").html(trans.signup.emailConfirmSuccess);
        else $element.find("> .title").html(trans.signup.emailConfirmFailed);
    }
}
